/* eslint-disable @typescript-eslint/no-empty-object-type */

/**
 * Extracts dynamic route parameters from a route string and maps them to a typed object.
 *
 * This utility type parses a route pattern (e.g. `/users/:userId/posts/:postId`)
 * and infers the parameter names, returning an object type where each parameter
 * is a key with a `string | number` value.
 *
 * @template T - The route string to extract parameters from.
 *
 * @example
 * type Params = ExtractRouteParams<'/users/:userId/posts/:postId'>
 * // Result:
 * // {
 * //   userId: string | number;
 * //   postId: string | number;
 * // }
 *
 * @example
 * type Params = ExtractRouteParams<'/about'>
 * // Result: {}
 *
 * @example
 * type Params = ExtractRouteParams<string>
 * // Result: Record<string, string | number>
 *
 * @remarks
 * - Parameters are identified by the `:` prefix in route segments.
 * - If the route type is a generic `string`, the result falls back to a generic record.
 * - Supports multiple parameters across different route segments.
 *
 * @returns An object type where keys are route parameter names and values are `string | number`.
 */
type ExtractRouteParams<T extends string> = string extends T
  ? Record<string, string | number>
  : T extends `${string}:${infer Param}/${infer Rest}`
    ? { [K in Param | keyof ExtractRouteParams<Rest>]: string | number }
    : T extends `${string}:${infer Param}`
      ? { [K in Param]: string | number }
      : {}

export function routeParser<T extends string>(route: T, params: ExtractRouteParams<T>) {
  return route
    .split('/')
    .map((segment) => {
      if (segment.startsWith(':')) {
        const paramName = segment.slice(1)
        return params[paramName as keyof ExtractRouteParams<T>]
      }
      return segment
    })
    .join('/')
}
